import { DEFAULT_TARIFFS, type Tariff, TariffTypes, type Tariffs } from '@/constants/tariff';
// eslint-disable-next-line no-duplicate-imports
import { type CostItem } from '@/constants/Tariffs';
import { type AbPackage } from '@/types/types';
import { getAbTestsCost, getPhrasesCost, getStoresAdvCost, getStoresCost } from '@/utils/costs';
import { CommonModuleTypes, type CommonPermissionOut, type CommonUserMeOut } from '@marpla/api-sdk';
import { type PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { format } from 'date-fns';

/** calculated user costs */
// export interface UserCard {
//   'id': number;
//   'card_num': string;
//   'card_code': string;
//   'card_start': Date;
//   'card_end': Date;
// }

export interface UserCard {
  id: number;
  userId: number;
  provider: string;
  token: string;
  first_six: string;
  last_four: string;
  brand: string;
  issuer: string;
  exp_month: number;
  exp_year: number;
  is_default: boolean;
  status: 'active' | 'inactive' | string;
  payment_method: string;
  created_at: Date;
  updated_at: Date;
  deleted_at: Date | null;
}

export interface AutopaySettingsRaw {
  id: number;
  user_id: number;
  card_id: number;
  currency: string;
  threshold: number;
  topup_amount: number;
  is_enabled: boolean;
  is_enabled_ab_packages: boolean;
  is_was_enabled: boolean;
  created_at: number;
  updated_at: number;
}

export interface AutopaySettings {
  id: number;
  userId: number;
  cardId: number;
  currency: string;
  threshold: number;
  amount: number;
  enabled: boolean;
  enabledAbPackages: boolean;
  createdAt: Date;
  updatedAt: Date;
  wasEnabled: boolean;
}

export interface GetAutopaySettingsResponse {
  success: boolean;
  data?: AutopaySettingsRaw;
}

export interface UserQuota {
  'id': number;
  'count': number;
  'expiredAt': Date;
  'userId': number;
  'price': number;
  'type': 'paid' | 'free' | 'deposit' | null;
}

export interface UserQuotas {
  quotas: UserQuota[];
  unlimited: boolean;
}

const ABTEST_UNLIMITED_QUOTA_COUNT_THRESHOLD = 10;

const getIsPaidUnlimitedAbTestsQuota = (
  quota: Pick<UserQuota, 'count' | 'type'> | null | undefined,
): boolean => quota?.type === 'paid' && quota.count > ABTEST_UNLIMITED_QUOTA_COUNT_THRESHOLD;

export interface User {
  accountId: string | undefined;
  email: string | undefined;
  phone: string | undefined;
  registrationDate: string | undefined;
  paidReferrals: number;
  totalReferrals: number;
  refferalUrl: string;
  uniqueQueries: number;
  projects: number;
  phrases: number;
  stores: number;
  salesStores: number;
  salesMonth: number;
  /** current user's abTests */
  abTests: UserQuotas;
  weeklyOrderSum: number;
  weeklySaleSum: number;
  balance: number | undefined;
  balanceApproximateDayCount: number | null;
  unlimitedFinanceInfo?: boolean;
  tariffs: {
    phrase_count_min: number;
    phrase_count_max: number;
    value: number;
    title: string;
    price_per_day: number;
    price: number;
  }[];
  defaultTariffs: Tariff[];
  tariffFreeDays: number;
  dayPrice: number;
  bonusTime: {
    minutes: number;
    hours: number;
    days: number;
    totalMinutes: number;
    hasTime: boolean;
  };
  calculateTariff: undefined | number;
  isActiveAB: boolean;
  expirationABDate: string | false;
  isLoadingAccessSensitiveData: boolean;
  isLoadingUserBasicData: boolean;
  isLoadingFinanceData: boolean;
  currentTariff: Tariff | null;
  currentTariffKey: Tariffs | null;
  cards: UserCard[];
  permissions: CommonPermissionOut[];
  autopaySettings: AutopaySettings | null;
  userId?: number;
  abPackage: AbPackage | null;
  activatedModuleTypes: CommonModuleTypes[];
}

export interface UserMeOut {
  /**
   * { integer: true, min: 1 }
   */
  chId: number;

  /**
   * { type: 'email' }
   */
  email: string;

  /**
   * @default false
   */
  emailConfirmed: boolean;

  /**
   * { type: 'phone' }
   */
  phone?: string;
  tariffActivated: boolean;
  abtestTariffActivated: boolean;
  activatedModuleTypes: CommonModuleTypes[];
  tgBotConnected: boolean;
  tgUsername?: string;
  permissions: CommonPermissionOut[];
  abPackage?: AbPackage | null;
}

export interface UserMeType extends CommonUserMeOut {
  currentAccountId?: string;
}

export interface Action<P> {
  type: string;
  payload: P;
}

const initialState: User = {
  accountId: undefined,
  email: undefined,
  phone: undefined,
  registrationDate: undefined,
  paidReferrals: 0,
  totalReferrals: 0,
  refferalUrl: '',
  uniqueQueries: 0,
  projects: 0,
  phrases: 0,
  stores: 0,
  abTests: { quotas: [], unlimited: false },
  salesStores: 0,
  salesMonth: 0,
  weeklyOrderSum: 0,
  weeklySaleSum: 0,
  balance: undefined,
  balanceApproximateDayCount: null,
  unlimitedFinanceInfo: undefined,
  tariffs: [],
  tariffFreeDays: 0,
  dayPrice: 0,
  bonusTime: {
    minutes: 0,
    hours: 0,
    days: 0,
    totalMinutes: 0,
    hasTime: false,
  },
  calculateTariff: undefined,
  isActiveAB: false,
  expirationABDate: false,
  isLoadingAccessSensitiveData: true,
  isLoadingUserBasicData: true,
  isLoadingFinanceData: true,
  currentTariff: null,
  currentTariffKey: null,
  defaultTariffs: Object.values(DEFAULT_TARIFFS),
  cards: [],
  permissions: [],
  autopaySettings: null,
  userId: undefined,
  abPackage: null,
  activatedModuleTypes: [],
};

// @ts-ignore
export const userSlice = createSlice({
  name: 'userSlice',
  initialState,
  reducers: {
    // TODO setUser error
    // @ts-ignore
    setUser: (state, action: Action<Partial<User>>) => {
      const updatedState = { ...state, ...action.payload };

      const tariffType = state.currentTariffKey;

      const calculatedTariff = tariffType ? DEFAULT_TARIFFS[tariffType] : null;

      const preservedCosts = state.currentTariff
        ? {
            abTestsCosts: state.currentTariff.abTestsCosts,
            abTestsCostsWithoutTariff: state.currentTariff.abTestsCostsWithoutTariff,
            phrasesCosts: state.currentTariff.phrasesCosts,
            projectsCosts: state.currentTariff.projectsCosts,
          }
        : {};

      return {
        ...updatedState,
        currentTariff: calculatedTariff ? { ...calculatedTariff, ...preservedCosts } : null,
      };
    },
    setCurrentTariff: (state, action: Action<Tariff>) => {
      state.currentTariff = action.payload;
    },
    updateCurrentTariff: (state, action: Action<Partial<Tariff>>) => {
      state.currentTariff &&= {
        ...state.currentTariff,
        ...action.payload,
      };
    },
    removeOrganizationByAccountId(state, action: PayloadAction<string>) {
      const accountId = action.payload;
      state.permissions = state.permissions.filter((p) => p.account._id !== accountId);
    },
    setAutopayEnabled(state, action: PayloadAction<boolean>) {
      if (!state.autopaySettings) return;

      state.autopaySettings.enabled = action.payload;
      state.autopaySettings.updatedAt = new Date();
    },
    setAutopayAbPackagesEnabled(state, action: PayloadAction<boolean>) {
      if (!state.autopaySettings) return;

      state.autopaySettings.enabledAbPackages = action.payload;
      state.autopaySettings.updatedAt = new Date();
    },

    updateAutopayPartial(state, action: PayloadAction<Partial<AutopaySettings>>) {
      if (!state.autopaySettings) return;
      state.autopaySettings = {
        ...state.autopaySettings,
        ...action.payload,
        updatedAt: new Date(),
      };
    },
    setAbPackage: (state, action: PayloadAction<AbPackage | null>) => {
      state.abPackage = action.payload;
    },
  },
});

export const selectUser = (state: { user: User }): User => state.user;
export const selectUserEmail = (state: { user: User }): string => state.user.email ?? '';
export const selectUserId = (state: { user: User }): number => state.user.userId ?? 0;
export const selectCurrentTariff = (state: { user: User }): Tariff | null =>
  state.user.currentTariff;
export const selectIsPaidTariff = (state: { user: User }): boolean =>
  state.user.currentTariff?.type === TariffTypes.PAID;
export const selectUserPhrases = (state: { user: User }): number => state.user.phrases;
export const selectUserStores = (state: { user: User }): number => state.user.stores;
export const selectUserAbTestsCount = (state: { user: User }): number =>
  state.user.abTests.quotas.reduce((acc, v) => acc + v.count, 0);
export const selectHasActiveUnlimitedAbTestsQuota = (state: { user: User }): boolean =>
  state.user.abTests.quotas.some(getIsPaidUnlimitedAbTestsQuota);
export const selectIsUnlimitedFinanceInfo = (state: { user: User }): boolean =>
  Boolean(state.user.unlimitedFinanceInfo);
export const notActivatedModuleTypes = (state: { user: User }): boolean =>
  !(
    state.user.activatedModuleTypes.includes(CommonModuleTypes.BIDDER) ||
    state.user.activatedModuleTypes.includes(CommonModuleTypes.ABTEST)
  );

export const selectUserAdvStores = (state: { user: User }): number => state.user.salesStores;
export const selectUserAutopaySettings = (state: { user: User }): AutopaySettings | null =>
  state.user.autopaySettings;
export const selectDayPrice = (state: { user: User }): number => state.user.dayPrice;
export const selectBaseTariffPrice = createSelector(
  selectCurrentTariff,
  (tariff) => tariff?.base_price ?? 0,
);
export const selectCurrentTariffDisplayName = createSelector(
  selectCurrentTariff,
  (tariff) => tariff?.displayName ?? '',
);
export const selectUserUniqueQueries = (state: { user: User }): number =>
  state.user.uniqueQueries ?? 0;
export const selectUserPermissions = (state: { user: User }): CommonPermissionOut[] =>
  state.user.permissions;
export const selectUserProjects = (state: { user: User }): number => state.user.projects ?? 0;
export const selectUserCurrentTariffKey = (state: { user: User }): Tariffs | null =>
  state.user.currentTariffKey;
export const selectUserSalesStoresCount = (state: { user: User }): number =>
  state.user.salesStores ?? 0;
export const selectUserAdvertisingStoresCount = (state: { user: User }): number =>
  state.user.stores ?? 0;
export const selectUserBalance = (state: { user: User }): number => state.user.balance ?? 0;
export const selectUserBonusHasTime = (state: { user: User }): boolean =>
  state.user.bonusTime?.hasTime;
export const selectUserBonusDays = (state: { user: User }): number =>
  state.user.bonusTime?.hasTime ? state.user.bonusTime.days : 0;
export const selectUserBonusHours = (state: { user: User }): number =>
  state.user.bonusTime?.hasTime ? state.user.bonusTime.hours : 0;
export const selectUserBonusMinutes = (state: { user: User }): number =>
  state.user.bonusTime?.hasTime ? state.user.bonusTime.minutes : 0;
export const selectAbTestsCosts = (state: { user: User }): CostItem[] =>
  state.user.currentTariff?.abTestsCosts ?? [];
export const selectAbTestsCostsWithoutTariff = (state: { user: User }): CostItem[] =>
  state.user.currentTariff?.abTestsCostsWithoutTariff ?? [];
export const selectVisibleDefaultTariffs = (state: { user: User }): Tariff[] =>
  state.user.defaultTariffs.filter(({ visible }) => visible);
export const selectCurrentTariffName = (state: { user: User }): string | null =>
  state.user.currentTariff?.name ?? null;

export const selectUserBalanceApproximateDayCount = (state: { user: User }): number | null =>
  state.user.balanceApproximateDayCount;

export const selectUserBonusTime = (state: {
  user: User;
}): {
  minutes: number;
  hours: number;
  days: number;
  totalMinutes: number;
  hasTime: boolean;
} => state.user.bonusTime;

export const selectUserCards = (state: {
  user: User;
}): {
  id: number;
  type: string;
  last4: string;
}[] =>
  state.user.cards.map(({ id, brand, last_four }) => ({
    id,
    type: brand,
    last4: last_four,
  }));

export const selectAbPackage = (state: { user: User }): AbPackage | null => state?.user.abPackage;

export const selectUserAbTestsWithDate = (state: { user: User }): string[] =>
  state.user.abTests.quotas.reduce((acc, v) => {
    acc.push(`${v.count}шт. до ${format(v.expiredAt, 'dd.MM')}`);

    return acc;
  }, [] as string[]);

export const selectActivePermission = (state: { user: User }): CommonPermissionOut | undefined => {
  const accountId = state.user.accountId;

  return state.user.permissions.find((p) => p.account._id === accountId);
};

export const selectActiveTokens = createSelector(selectActivePermission, (permission) => ({
  hasToken: permission?.hasToken === true,
  hasServiceToken: permission?.hasServiceToken === true,
}));

export const selectHasPaidABTests = createSelector([selectAbPackage], (abPackage): boolean => {
  const packages = abPackage?.packagesCounts ?? [];

  return packages.some((p) => p.type === 'paid' && p.executionsLeft > 0);
});

export const selectUserCosts = createSelector(
  [
    selectUserPhrases,
    selectCurrentTariff,
    selectUserStores,
    selectUserAdvStores,
    selectUserAbTestsCount,
  ],
  (phrases, currentTariff, stores, advStores, abTests) => ({
    abTests: getAbTestsCost(
      abTests,
      currentTariff?.features.ab_tests ?? 0,
      currentTariff?.abTestsCosts,
    ),
    seo: getPhrasesCost(phrases, currentTariff?.phrasesCosts),
    sales: getStoresCost(
      stores,
      currentTariff?.features?.max_stores_sales ?? 0,
      currentTariff?.additional_services_price ?? 0,
    ),
    adv: getStoresAdvCost(
      advStores,
      currentTariff?.features?.max_stores_advertising ?? 0,
      currentTariff?.additional_services_price ?? 0,
    ),
  }),
);

export const {
  setUser,
  setCurrentTariff,
  updateCurrentTariff,
  removeOrganizationByAccountId,
  setAutopayEnabled,
  updateAutopayPartial,
  setAbPackage,
  setAutopayAbPackagesEnabled,
} = userSlice.actions;

export default userSlice.reducer;
